import { useAppStore } from '@/stores/app-store'
import type { User } from '@/models/User'
import type { ApiResponse } from '@/api'
import { ApiHttpError } from '@/common/errors'

export function getUrl(path: string) {
  const appStore = useAppStore()
  return appStore.baseUrl + path
}

export function getApiUrl(apiPath: string) {
  return getUrl('api/' + apiPath)
}

export function getViewSubmissionUrl(submissionRef: string) {
  return getUrl('applications/view?reference=' + submissionRef)
}

export function formatDateForVueGoodTable(_date: string | undefined) {
  if (!_date) {
    return ''
  }

  const date = new Date(_date)
  return formatDate(date)
}

export function formatDate(_date: Date) {
  const month = _date.getMonth() + 1
  const date = _date.getDate()
  const hour = _date.getHours()
  const minute = _date.getMinutes()

  return (
    _date.getFullYear() +
    '-' +
    (month > 9 ? '' : '0') +
    month +
    '-' +
    (date > 9 ? '' : '0') +
    date +
    ' ' +
    (hour > 9 ? '' : '0') +
    hour +
    ':' +
    (minute > 9 ? '' : '0') +
    minute
  )
}

export function showServerDate(serverDateStr: string) {
  return formatDate(new Date(serverDateStr))
}

// Copied from /public/HelperFunctions.js
export function formatIsoDate(isodate: string | undefined): string {
  if (!isodate || isodate === '') return ''
  const date = new Date(isodate)
  const monthIndex = date.getMonth()
  let mins: number | string = date.getMinutes()
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ]
  if (mins < 10) mins = '0' + mins

  const result =
    months[monthIndex] +
    ' ' +
    date.getDate() +
    ', ' +
    date.getFullYear() +
    ' - ' +
    date.getHours() +
    ':' +
    mins
  return result
}

// All pages call this when they do an AJAX request, the server sends back the current user name and their perms.
export function updateUser(user?: User | false) {
  const appStore = useAppStore()

  let permString = null
  let userName = null

  // if false was provided (for logout purpose), no need to load any data, will skip to next block

  if (typeof user === 'undefined') {
    // When the page first loads, this function is called without any parameter, so try to load from memory.
    userName = window.localStorage.getItem('currentAsdUserName')
    permString = window.localStorage.getItem('currentAsdUserPerms')

    if (userName) {
      //
    }
  } else {
    // if data was provided
    if (user) {
      userName = user.display_name

      if (user.permissions) {
        permString = Object.values(user.permissions).join(' ')
        window.localStorage.setItem('currentAsdUserPerms', permString)
      }
    }
  }

  if (userName) {
    window.localStorage.setItem('currentAsdUserName', userName)
    appStore.user = user as User
    appStore.cachedUsername = userName
  } else {
    window.localStorage.removeItem('currentAsdUserName')
    appStore.user = null
    appStore.cachedUsername = ''
  }

  if (permString) {
    window.localStorage.setItem('currentAsdUserPerms', permString)
    document.body.setAttribute('data-perms', permString)
  } else {
    window.localStorage.removeItem('currentAsdUserPerms')
    document.body.removeAttribute('data-perms')
  }
}

export function updateDevMode(deploymentMode?: string) {
  // const deploymentMode = respData?.deployment_mode || ''
  if (typeof deploymentMode === 'undefined') {
    deploymentMode = window.localStorage.getItem('defaultDeployMode') || ''
  }

  if (deploymentMode) {
    document.body.setAttribute('data-deploy-mode', deploymentMode)
    window.localStorage.setItem('defaultDeployMode', deploymentMode)
  } else {
    document.body.removeAttribute('data-deploy-mode')
    window.localStorage.removeItem('defaultDeployMode')
  }
}

function _getCookie(name: string) {
  const c = document.cookie.split('; ')
  const cookies = {} as { [index: string]: string }

  for (const cookie of c) {
    const _parts = cookie.split('=')
    cookies[_parts[0]!] = _parts[1] ?? ''
  }

  return cookies[name]
}

function _randomCryptoString(bytes = 8) {
  const arr = new Uint8Array(bytes)
  // let crypto = (window.crypto || window.msCrypto);
  // crypto.getRandomValues(arr);
  window.crypto.getRandomValues(arr)
  return [].slice.call(arr).join('')
}

export function generateCsrfHeaders() {
  let co = _getCookie('CSRF-TOKEN')
  if (co == null || co === '') {
    co = _randomCryptoString()
    document.cookie = 'CSRF-TOKEN=' + co + '; path=/ ; SameSite=Strict'
  }
  return { 'X-CSRF-TOKEN': co }
}

export function doLogout() {
  doAjaxRequest<void>('POST', 'logout', null, generateCsrfHeaders())
    .then(() => {
      window.location.replace(getUrl('logout'))
    })
    .catch((error: Error) => {
      alert(error.message)
    })
}

export function doAjaxRequest<TData>(
  method: string,
  url: string,
  body?: object | null,
  headers?: object | null
): Promise<ApiResponse<TData>> {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 15000)
  const requestHeaders = new Headers(headers as HeadersInit | undefined)
  let requestBody: BodyInit | undefined

  if (body instanceof FormData || body instanceof URLSearchParams || body instanceof Blob) {
    requestBody = body
  } else if (body) {
    requestHeaders.set('Content-Type', 'application/json')
    requestBody = JSON.stringify(body)
  }

  return fetch(getApiUrl(url), {
    method,
    headers: requestHeaders,
    body: requestBody,
    signal: controller.signal
  })
    .then(async (response) => {
      const responseText = await response.text()
      let responseData: ApiResponse<TData> | undefined

      if (responseText) {
        try {
          const parsedResponse = JSON.parse(responseText) as ApiResponse<TData> | null
          if (parsedResponse && typeof parsedResponse === 'object') {
            responseData = parsedResponse
          }
        } catch {
          responseData = undefined
        }
      }

      if (!response.ok) {
        throw new ApiHttpError(
          response.status,
          responseData?.error?.message ?? response.statusText,
          undefined,
          responseData
        )
      }

      if (responseText && !responseData) {
        throw new ApiHttpError(response.status, 'Invalid server response', 'ERR_BAD_RESPONSE')
      }

      const successfulResponse =
        responseData ?? ({ data: undefined as TData } satisfies ApiResponse<TData>)

      updateUser(successfulResponse.user)
      updateDevMode(successfulResponse.deployment_mode)

      return successfulResponse
    })
    .catch((error) => {
      if (error instanceof ApiHttpError) {
        if (error.status === 401) {
          window.location.replace(
            getApiUrl('system/authenticate?return=' + encodeURIComponent(window.location.href))
          )
          return Promise.reject(new Error('Unauthenticated'))
        }

        return Promise.reject(error)
      }

      if (error instanceof DOMException && error.name === 'AbortError') {
        return Promise.reject(new ApiHttpError(0, 'timeout of 15000ms exceeded', 'ECONNABORTED'))
      }

      return Promise.reject(new ApiHttpError(0, 'Connection error', 'ERR_NETWORK'))
    })
    .finally(() => window.clearTimeout(timeout))
}

export const formatStringRevertUppercase = (text: string): string => {
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

// Helper function to escape HTML to prevent XSS
export const escapeHtml = (text: string | number | null | undefined): string => {
  if (!text && text !== 0) return ''
  const stringText = String(text)
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  }
  return stringText.replace(/[&<>"']/g, (m) => map[m] ?? m)
}

// Helper function to check if a value is a valid number
export const isValidNumber = (value: string | number): boolean => {
  if (value === '') return false
  const num = Number(value)
  return !isNaN(num) && isFinite(num)
}

export function determineErrorMessage(error: Error): string {
  if (
    error.message.toLowerCase().includes('timeout') &&
    error.message.toLowerCase().includes('exceeded')
  ) {
    return 'Connection timeout'
  }

  if (!(error instanceof ApiHttpError)) return error.message
  return error.responseData?.error?.message || error.message
}

export function withApiErrorHandling<TData>(request: Promise<TData>): Promise<TData> {
  return request.catch((error) => {
    throw new Error(determineErrorMessage(error))
  })
}
